import { Link, Outlet, useLocation, Navigate } from 'react-router';
import { useAuth } from '@/lib/auth-context';
import { useQueryClient } from '@tanstack/react-query';
import { colors, accent } from '@/lib/tokens';
import { isAllowedAvatarUrl } from '@/lib/avatar-url';
import { Spinner } from '@/components/ui/spinner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { RecostLogo } from '@/components/RecostLogo';
import { SignOutButton } from '@/components/dashboard/SignOutButton';

const NAV = [
  { href: '/dashboard/projects', label: 'Projects' },
  { href: '/dashboard/getting-started', label: 'Setup Guide' },
  { href: '/dashboard/parser', label: 'Auto Parser', adminOnly: true },
  { href: '/dashboard/account', label: 'Account' },
] as const;

function useBreadcrumb() {
  const { pathname } = useLocation();
  const qc = useQueryClient();

  const projectDetailMatch = pathname.match(/^\/dashboard\/projects\/([^/]+)/);
  if (projectDetailMatch) {
    const id = projectDetailMatch[1];
    const list = qc.getQueryData<{ id: string; name: string }[]>(['dashboard-projects']);
    const name = list?.find(p => p.id === id)?.name
      ?? qc.getQueryData<{ id: string; name: string }>(['dashboard-project', id])?.name
      ?? '…';
    return { segments: [
      { label: 'Projects', href: '/dashboard/projects' },
      { label: name },
    ]};
  }

  const match = NAV.find(n => pathname.startsWith(n.href));
  return { segments: [{ label: match?.label ?? 'Dashboard' }] };
}

function FullScreenSpinner() {
  return (
    <div className="flex h-screen items-center justify-center" style={{ backgroundColor: colors.bgPage }}>
      <Spinner />
    </div>
  );
}

function NavItem({ href, label }: { href: string; label: string }) {
  const { pathname } = useLocation();
  const isActive = pathname.startsWith(href);

  return (
    <Link
      to={href}
      className="flex items-center px-4 py-2.5 text-[14px] transition-all duration-150 focus-visible:outline-none focus-visible:shadow-[0_0_0_1.5px_rgba(242,107,29,0.5)] rounded-sm"
      style={{
        textDecoration: 'none',
        fontWeight: isActive ? 500 : 400,
        background: isActive ? 'rgba(242, 107, 29, 0.15)' : 'transparent',
        borderLeft: isActive ? `2px solid ${accent}` : '2px solid transparent',
        color: isActive ? colors.textPrimary : colors.textMuted,
      }}
    >
      {label}
    </Link>
  );
}

export default function DashboardLayout() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { pathname } = useLocation();
  const { segments } = useBreadcrumb();

  if (isLoading) return <FullScreenSpinner />;
  if (!isAuthenticated) {
    sessionStorage.setItem('ecoapi_redirect', pathname);
    return <Navigate to="/login" replace />;
  }

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const visibleNav = NAV.filter(item => !('adminOnly' in item && item.adminOnly) || isAdmin);

  return (
    <TooltipProvider>
    <div className="flex flex-col h-screen overflow-hidden" style={{ backgroundColor: colors.bgPage }}>

      {/* Top header */}
      <header
        className="flex-shrink-0 flex items-center px-6 h-14 min-w-0"
        style={{ borderBottom: `1px solid ${colors.borderSubtle}`, background: colors.bgHeader }}
      >
        <Link to="/" className="flex items-center hover:opacity-75 transition-opacity flex-shrink-0" style={{ textDecoration: 'none' }}>
          <RecostLogo size={22} wordmarkColor={colors.textPrimary} />
        </Link>
        <div className="flex items-center min-w-0 overflow-hidden">
          {segments.map((seg, i) => (
            <span key={i} className="flex items-center min-w-0">
              <span className="mx-2 sm:mx-3 text-sm flex-shrink-0" style={{ color: colors.borderHover }}>/</span>
              {seg.href ? (
                <Link
                  to={seg.href}
                  className="text-sm text-[#78716c] hover:text-[#0a0a0a] transition-colors flex-shrink-0"
                  style={{ textDecoration: 'none' }}
                >
                  {seg.label}
                </Link>
              ) : (
                <span className="text-sm truncate" style={{ color: colors.textPrimary }}>{seg.label}</span>
              )}
            </span>
          ))}
        </div>
        <div className="ml-auto" />
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar — desktop only */}
        <aside
          className="hidden md:flex flex-col w-56 flex-shrink-0"
          style={{ borderRight: `1px solid ${colors.borderSubtle}`, background: colors.bgHeader }}
        >
          <nav className="flex-1 py-4 space-y-0.5">
            {visibleNav.map(({ href, label }) => (
              <NavItem key={href} href={href} label={label} />
            ))}
          </nav>

          {/* User section */}
          <div className="px-3 py-4" style={{ borderTop: `1px solid ${colors.borderSubtle}` }}>
            {user && (
              <div className="flex items-center gap-3 px-4 py-3 mb-1">
                {isAllowedAvatarUrl(user.avatarUrl) ? (
                  <img
                    src={user.avatarUrl!}
                    alt={user.name ?? ''}
                    className="w-7 h-7 rounded-full flex-shrink-0"
                    loading="lazy"
                    decoding="async"
                    style={{ outline: `1px solid ${colors.borderDefault}` }}
                  />
                ) : (
                  <div
                    className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-[11px] font-semibold"
                    style={{ background: colors.accentSubtle, color: accent }}
                  >
                    {(user.name ?? user.email)[0].toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium truncate" style={{ color: colors.textPrimary }}>{user.name}</p>
                  <p className="text-[11px] truncate mt-0.5 font-mono" style={{ color: colors.textMuted }}>
                    {user.email}
                  </p>
                </div>
              </div>
            )}
            <SignOutButton variant="sidebar" />
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto pb-20 md:pb-0 no-scrollbar">
          <Outlet />
        </main>
      </div>

      {/* Bottom nav — mobile only */}
      <nav
        className="flex md:hidden fixed bottom-0 inset-x-0 z-50 items-center justify-around px-2 py-1"
        style={{ background: colors.bgPage, borderTop: `1px solid ${colors.borderSubtle}` }}
      >
        {visibleNav.map(({ href, label }) => {
          const isActive = pathname.startsWith(href);
          return (
            <Link key={href} to={href} className="focus-visible:outline-none focus-visible:shadow-[0_0_0_1.5px_rgba(242,107,29,0.5)] rounded-md" style={{ textDecoration: 'none', flex: 1 }}>
              <div
                className="flex flex-col items-center gap-1 py-2.5 rounded-md mx-1 transition-all duration-150"
                style={{
                  color: isActive ? accent : colors.textMuted,
                  background: isActive ? colors.accentSubtle : 'transparent',
                }}
              >
                <span style={{ fontSize: '10px', fontWeight: isActive ? 600 : 400 }}>{label}</span>
              </div>
            </Link>
          );
        })}
      </nav>
    </div>
    </TooltipProvider>
  );
}
